import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * RollingOdometerInput - A rolling number picker like old car odometers
 * Shows individual digit positions that can be adjusted separately.
 * 
 * Props:
 * - value: number (current odometer reading)
 * - onChange: (value: number) => void
 * - digits: number (default 6, for readings up to 999,999)
 * - disabled: boolean
 * - minValue: number (optional, prevents going below this)
 */
const RollingOdometerInput = ({ 
  value = 0, 
  onChange, 
  digits = 6, 
  disabled = false,
  minValue = null,
  label = "Odometer"
}) => {
  // Convert value to array of digits (padded with zeros)
  const valueToDigits = useCallback((val) => {
    const str = String(Math.max(0, Math.floor(val))).padStart(digits, '0');
    return str.slice(-digits).split('').map(Number);
  }, [digits]);

  const [digitValues, setDigitValues] = useState(() => valueToDigits(value));
  const [activeDigit, setActiveDigit] = useState(null);
  const holdIntervalRef = useRef(null);

  // Update when external value changes
  useEffect(() => {
    setDigitValues(valueToDigits(value));
  }, [value, valueToDigits]);

  // Calculate the numeric value from digits
  const digitsToValue = (arr) => {
    return parseInt(arr.join(''), 10) || 0;
  };

  // Increment a specific digit
  const incrementDigit = (index) => {
    if (disabled) return;
    
    setDigitValues(prev => {
      const newDigits = [...prev];
      newDigits[index] = (newDigits[index] + 1) % 10;
      
      const newValue = digitsToValue(newDigits);
      onChange?.(newValue);
      return newDigits;
    });
  };

  // Decrement a specific digit
  const decrementDigit = (index) => {
    if (disabled) return;
    
    setDigitValues(prev => {
      const newDigits = [...prev];
      newDigits[index] = (newDigits[index] - 1 + 10) % 10;
      
      const newValue = digitsToValue(newDigits);
      
      // Check minimum value constraint
      if (minValue !== null && newValue < minValue) {
        return prev; // Don't change if below minimum
      }
      
      onChange?.(newValue);
      return newDigits;
    });
  };

  // Handle hold-to-repeat
  const startHold = (action, index) => {
    action(index);
    holdIntervalRef.current = setInterval(() => action(index), 150);
  };

  const stopHold = () => {
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => stopHold();
  }, []);

  // Position labels
  const getPositionLabel = (index) => {
    const positions = digits - index;
    switch (positions) {
      case 6: return '100K';
      case 5: return '10K';
      case 4: return '1K';
      case 3: return '100';
      case 2: return '10';
      case 1: return '1';
      default: return '';
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-slate-300">{label}</label>
      )}
      <div className="flex justify-center gap-1 sm:gap-2 bg-slate-800/50 p-3 sm:p-4 rounded-xl border border-slate-700">
        {digitValues.map((digit, index) => (
          <div 
            key={`digit-${index}`} 
            className="flex flex-col items-center"
          >
            {/* Position label */}
            <span className="text-[10px] sm:text-xs text-slate-500 mb-1 font-mono">
              {getPositionLabel(index)}
            </span>
            
            {/* Up button */}
            <button
              type="button"
              disabled={disabled}
              onMouseDown={() => startHold(incrementDigit, index)}
              onMouseUp={stopHold}
              onMouseLeave={stopHold}
              onTouchStart={() => startHold(incrementDigit, index)}
              onTouchEnd={stopHold}
              className={`
                w-8 h-6 sm:w-10 sm:h-8 flex items-center justify-center
                rounded-t-lg transition-colors
                ${disabled 
                  ? 'bg-slate-700/50 text-slate-600 cursor-not-allowed' 
                  : 'bg-slate-700 text-slate-300 hover:bg-emerald-600 hover:text-white active:bg-emerald-700'
                }
              `}
              data-testid={`odometer-up-${index}`}
            >
              <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            
            {/* Digit display */}
            <div 
              className={`
                w-8 h-10 sm:w-10 sm:h-12 flex items-center justify-center
                bg-slate-900 border-x border-slate-600
                font-mono text-xl sm:text-2xl font-bold
                ${disabled ? 'text-slate-500' : 'text-white'}
                ${activeDigit === index ? 'ring-2 ring-emerald-500' : ''}
              `}
              onClick={() => !disabled && setActiveDigit(index === activeDigit ? null : index)}
            >
              <span className="relative">
                {digit}
              </span>
            </div>
            
            {/* Down button */}
            <button
              type="button"
              disabled={disabled}
              onMouseDown={() => startHold(decrementDigit, index)}
              onMouseUp={stopHold}
              onMouseLeave={stopHold}
              onTouchStart={() => startHold(decrementDigit, index)}
              onTouchEnd={stopHold}
              className={`
                w-8 h-6 sm:w-10 sm:h-8 flex items-center justify-center
                rounded-b-lg transition-colors
                ${disabled 
                  ? 'bg-slate-700/50 text-slate-600 cursor-not-allowed' 
                  : 'bg-slate-700 text-slate-300 hover:bg-emerald-600 hover:text-white active:bg-emerald-700'
                }
              `}
              data-testid={`odometer-down-${index}`}
            >
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        ))}
        
        {/* KM label */}
        <div className="flex flex-col justify-center ml-2">
          <span className="text-sm sm:text-base font-bold text-emerald-500">km</span>
        </div>
      </div>
      
      {/* Numeric display for accessibility */}
      <div className="text-center text-sm text-slate-400">
        Reading: <span className="font-mono font-bold text-white">{digitsToValue(digitValues).toLocaleString()}</span> km
      </div>
    </div>
  );
};

export default RollingOdometerInput;
